import {
  type LocationData,
  Logger,
  type UnsavedLocation,
  type CustomLocationId,
  type LatLng,
  type LocationRemoveSuccess,
  type LocationRemoveInput,
  DAY,
  HOUR,
  toResponseError,
  type LocationAddInput,
  type LocationGetSuccess,
} from '@terros/common'
import { useQueryClient } from '@tanstack/react-query'
import { useTantalimQuery } from '../query'
import { useTantalimMutation } from '../mutation'
import { customLocationRemoveCheckKey, customLocationsKey } from '../keys'
import {
  type AddHookResponse,
  type GetHookResponse,
  mutationAdd,
  mutationRemove,
  type RemoveHookResponse,
} from '../hooks'
import { useLocationCache } from './cache'

const logger = new Logger('locations/add')
// logger.level = 'verbose'

export function useAddLocation(): AddHookResponse<UnsavedLocation, LocationData> {
  const mutation = useTantalimMutation<LocationAddInput, LocationGetSuccess>({ url: '/location/add' })
  const cache = useLocationCache()

  async function add(unsavedLocation: UnsavedLocation): Promise<LocationData> {
    try {
      logger.verbose('Adding custom location', unsavedLocation)
      const output = await mutation.mutateAsync(unsavedLocation)
      cache.invalidateSectorCache(output.location.latlng)
      return output.location
    } catch (error) {
      throw toResponseError(error)
    }
  }

  return mutationAdd(mutation, add)
}

type RemoveCustomLocationInput = {
  locationId: CustomLocationId
  latLng: LatLng
}

export function useRemoveCustomLocation(): RemoveHookResponse<RemoveCustomLocationInput> {
  const mutation = useTantalimMutation<RemoveCustomLocationInput, LocationRemoveSuccess>({ url: '/location/remove' })
  const cache = useLocationCache()
  const queryClient = useQueryClient()
  async function remove(input: RemoveCustomLocationInput): Promise<void> {
    try {
      logger.verbose('Removing custom location', input)
      await mutation.mutateAsync(input)
      cache.invalidateSectorCache(input.latLng)
      cache.clearItem(input.locationId)
      queryClient.invalidateQueries({ queryKey: customLocationsKey })
    } catch (error) {
      throw toResponseError(error)
    }
  }

  return mutationRemove(mutation, remove)
}

export function useRemoveCustomLocationSoftCheck(locationId: CustomLocationId): GetHookResponse<boolean> {
  return useTantalimQuery<LocationRemoveInput, LocationRemoveSuccess, boolean>({
    url: '/location/remove',
    queryKey: customLocationRemoveCheckKey(locationId),
    input: { locationId, check: true },
    convertResponse: (data) => Boolean(data.canDelete),
    options: {
      enabled: Boolean(locationId),
      gcTime: DAY,
      staleTime: HOUR,
      retry: false,
    },
  })
}
